// @systeme: interface
// Les gestes sur un élément (fait, reporter, corriger, supprimer, signaler) et les
// feuilles qui vont avec, partagés par Aujourd'hui et À venir. Un seul endroit.
import { useEffect, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router';
import type { Element, Membre, OptionReport } from '@foyer/shared';
import { api } from '../lib/api';
import { jourDe } from '../lib/dates';
import { textes } from '../lib/textes';
import { useDialoguesPreparation } from './DialoguesPreparation';
import { FeuilleElement, type ValeursElement } from './FeuilleElement';
import { FeuilleReport } from './FeuilleReport';
import { FormulaireRetour, type CibleDuRetour } from './FormulaireRetour';
import { LigneElement } from './LigneElement';
import { useMessages } from './Messages';

type Feuille =
  | { type: 'aucune' }
  | { type: 'creer' }
  | { type: 'corriger'; element: Element }
  | { type: 'reporter'; element: Element };

interface Options {
  jourAujourdhui: string;
  jourDemain: string;
  /** Rappelé après chaque geste réussi, pour recharger l'écran. */
  recharger(): Promise<void>;
  /** Le chemin de l'écran, pour situer un retour. */
  ecran: string;
}

export function useActionsElements({ jourAujourdhui, jourDemain, recharger, ecran }: Options) {
  const { dire, direErreur } = useMessages();
  const navigate = useNavigate();
  const { apresDateChangee, apresFait, dialogues } = useDialoguesPreparation({ recharger });
  const [feuille, setFeuille] = useState<Feuille>({ type: 'aucune' });
  const [ouverture, setOuverture] = useState(0);
  const [retour, setRetour] = useState<CibleDuRetour | undefined>();
  const [membres, setMembres] = useState<Membre[]>([]);

  // Les membres du foyer, une fois : « qui s'en charge » n'existe que s'ils sont plusieurs.
  useEffect(() => {
    let annule = false;
    api.moi
      .membres()
      .then((m) => !annule && setMembres(m))
      .catch(() => undefined);
    return () => {
      annule = true;
    };
  }, []);

  const ouvrir = (f: Feuille) => {
    setOuverture((n) => n + 1);
    setFeuille(f);
  };
  const fermer = () => setFeuille({ type: 'aucune' });

  async function agir(action: () => Promise<unknown>, confirmation?: string): Promise<void> {
    try {
      await action();
      if (confirmation) dire(confirmation);
      await recharger();
    } catch (e) {
      direErreur(e);
      throw e;
    }
  }
  const silencieux = (p: Promise<void>) => void p.catch(() => undefined);

  /** La ligne d'un élément, avec ou sans précision sous le titre. `ligne` s'utilise dans un map. */
  function ligneAvec(element: Element, sousTitre?: string): ReactNode {
    return (
      <LigneElement
        key={element.id}
        element={element}
        sousTitre={sousTitre}
        onFaire={() =>
          silencieux(agir(() => api.elements.faire(element.id)).then(() => apresFait(element)))
        }
        onAnnulerFait={() =>
          silencieux(agir(() => api.elements.modifier(element.id, { statut: 'a_faire' })))
        }
        onCorriger={() => ouvrir({ type: 'corriger', element })}
        onReporter={() => ouvrir({ type: 'reporter', element })}
        onSupprimer={() => silencieux(agir(() => api.elements.supprimer(element.id), 'Supprimé.'))}
        onSignaler={() => setRetour({ cible: 'element', cibleId: element.id })}
        onPreparer={() =>
          void navigate('/a-venir/preparations/nouvelle', {
            state: {
              elementId: element.id,
              titre: element.titre,
              jour: element.debutLe ? jourDe(element.debutLe) : null,
            },
          })
        }
      />
    );
  }

  const elementEnCours =
    feuille.type === 'corriger' || feuille.type === 'reporter' ? feuille.element : undefined;

  const feuilles = (
    <>
      <FeuilleElement
        key={ouverture}
        ouvert={feuille.type === 'creer' || feuille.type === 'corriger'}
        onFermer={fermer}
        jourAujourdhui={jourAujourdhui}
        jourDemain={jourDemain}
        element={feuille.type === 'corriger' ? feuille.element : undefined}
        membres={membres}
        onEnregistrer={(valeurs: ValeursElement) =>
          feuille.type === 'corriger'
            ? agir(() => api.elements.modifier(feuille.element.id, valeurs), 'C’est corrigé.').then(
                () => apresDateChangee(feuille.element),
              )
            : agir(
                () => api.elements.creer({ ...valeurs, type: 'tache', sensibilite: 'normale' }),
                textes.cestNote,
              )
        }
        onSupprimer={
          feuille.type === 'corriger'
            ? () => agir(() => api.elements.supprimer(feuille.element.id), 'Supprimé.')
            : undefined
        }
      />
      <FeuilleReport
        ouvert={feuille.type === 'reporter'}
        onFermer={fermer}
        onReporter={(quand: OptionReport, date?: string) =>
          elementEnCours
            ? agir(
                () => api.elements.reporter(elementEnCours.id, date ? { quand, date } : { quand }),
                'Reporté.',
              ).then(() => apresDateChangee(elementEnCours))
            : Promise.resolve()
        }
      />
      <FormulaireRetour
        ouvert={retour !== undefined}
        onFermer={() => setRetour(undefined)}
        ecran={ecran}
        cible={retour}
      />
      {dialogues}
    </>
  );

  const ligne = (element: Element): ReactNode => ligneAvec(element);

  return { ligne, ligneAvec, feuilles, ouvrirCreation: () => ouvrir({ type: 'creer' }) };
}
